/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { Batiment, Vehicule } from '../../../shared/types/game';
import { type InstanceRow } from '../components/interventions/instanceModel';
import { type LiveVehicleTrack } from '../components/WorldMap';
import { type VehicleCrewStatus, type CrewStatusStaff } from '../map/hooks/useVehicleCrewStatus';
import { type StatusStyle } from '../map/hooks/useStatusPalette';
import { useEngagementGroups } from '../map/hooks/useEngagementGroups';

/** Moniteur d'engins — table `.units-table` du prototype, données réelles. */

interface Props {
  playerVehicles: Vehicule[];
  buildings: Batiment[];
  interventions: InstanceRow[];
  liveVehicleTracks: LiveVehicleTrack[];
  crewStatus: VehicleCrewStatus[];
  staffById: Record<number, CrewStatusStaff>;
  palette: Record<string, StatusStyle>;
  selected: Set<number>;
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>;
  targetIntervention: InstanceRow | null;
}

type StatusFilter = 'all' | 'free' | 'busy' | 'ko';

const UnitsTable: React.FC<Props> = ({
  playerVehicles,
  buildings,
  interventions,
  liveVehicleTracks,
  crewStatus,
  staffById,
  palette,
  selected,
  setSelected,
  targetIntervention,
}) => {
  const [search, setSearch] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const { groups } = useEngagementGroups();

  const crewById = useMemo(() => {
    const m = new Map<number, VehicleCrewStatus>();
    for (const c of crewStatus) m.set(Number(c.vehicleId), c);
    return m;
  }, [crewStatus]);

  const buildingName = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of buildings || []) m.set(Number(b.id), b.nom || `Centre #${b.id}`);
    return (id: number | undefined) => (id != null && m.has(id) ? m.get(id)! : 'Hors centre');
  }, [buildings]);

  const assignmentOf = useMemo(() => {
    const m = new Map<number, InstanceRow>();
    for (const inv of interventions || []) {
      const ids = (inv as any).dispatch_player_vehicle_ids;
      if (Array.isArray(ids)) for (const id of ids) m.set(Number(id), inv);
    }
    return m;
  }, [interventions]);

  const etaOf = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const t of liveVehicleTracks || []) m.set(Number(t.vehicle_id), t.eta_remaining_sec ?? null);
    return m;
  }, [liveVehicleTracks]);

  const styleFor = (v: Vehicule): StatusStyle => {
    if (v.statut === 'disponible') return crewById.get(Number(v.id))?.armable === false ? palette.materiel : palette.disponible;
    if (v.statut === 'en_mission') return palette.en_mission;
    if (v.statut === 'en_maintenance') return palette.en_maintenance;
    return palette.panne;
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (playerVehicles || [])
      .filter((v) => (buildingFilter === 'all' ? true : Number(v.batiment_id) === buildingFilter))
      .filter((v) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'free') return v.statut === 'disponible';
        if (statusFilter === 'busy') return v.statut === 'en_mission';
        return v.statut === 'en_maintenance' || v.statut === 'panne';
      })
      .filter((v) => {
        if (!q) return true;
        return [v.nom, v.type, buildingName(v.batiment_id)].some((x) => String(x || '').toLowerCase().includes(q));
      });
  }, [playerVehicles, buildingFilter, statusFilter, search, buildingName]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allShown = rows.length > 0 && rows.every((v) => selected.has(Number(v.id)));

  const applyGroup = (compo: Record<string, number>) => {
    const picked = new Set<number>();
    for (const [type, need] of Object.entries(compo)) {
      const cands = rows
        .filter((v) => !picked.has(Number(v.id)))
        .filter((v) => String(v.type || '') === type)
        .filter((v) => v.statut === 'disponible' && crewById.get(Number(v.id))?.armable !== false);
      for (const v of cands.slice(0, Math.max(0, Number(need) || 0))) picked.add(Number(v.id));
    }
    setSelected(picked);
  };

  const crewCell = (v: Vehicule) => {
    const c = crewById.get(Number(v.id));
    if (!c) return <span className="muted">—</span>;
    if (c.engaged) return <span className="muted">engagé</span>;
    if (c.armable === false) {
      const motif = c.missing.map((m) => `${m.competenceName} — ${m.reason === 'absente' ? 'spécialité absente du centre' : 'titulaire indisponible'}`).join('\n');
      return <span className="is-mat" title={motif}>{c.missing.map((m) => m.competenceName).join(' · ') || 'équipage incomplet'}</span>;
    }
    if (c.noCompetenceRequired) return <span className="muted">sans équipage requis</span>;
    const names = c.crew.map((id) => {
      const s = staffById[id];
      return s ? `${s.grade ? s.grade + ' ' : ''}${s.prenom} ${s.nom}` : `#${id}`;
    });
    return <span title={names.join('\n')}>{names.length} pers.</span>;
  };

  return (
    <div className="panel units-panel">
      <div className="panel__head">
        <h2>Moniteur d&apos;engins</h2>
        <div className="filters">
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Indicatif, type, caserne…" />
          <select value={String(buildingFilter)} onChange={(e) => setBuildingFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">Toutes casernes</option>
            {(buildings || []).map((b) => (
              <option key={String(b.id)} value={Number(b.id)}>{b.nom}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">Tous statuts</option>
            <option value="free">Disponibles</option>
            <option value="busy">Engagés</option>
            <option value="ko">Indisponibles</option>
          </select>
        </div>
      </div>

      <div className="units-table-wrap scroll">
        <table className="units-table">
          <thead>
            <tr>
              <th className="w-check">
                <input
                  type="checkbox"
                  checked={allShown}
                  onChange={() => setSelected(allShown ? new Set() : new Set(rows.map((v) => Number(v.id))))}
                />
              </th>
              <th>Indicatif</th><th>Type</th><th className="w-st">Statut</th>
              <th>Affectation</th><th>Équipage</th><th>Remise</th><th className="w-dep">Dép.</th><th className="ta-r">Délai</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => {
              const st = styleFor(v);
              const materiel = v.statut === 'disponible' && crewById.get(Number(v.id))?.armable === false;
              const inv = assignmentOf.get(Number(v.id));
              const eta = etaOf.get(Number(v.id));
              return (
                <tr key={String(v.id)} className={selected.has(Number(v.id)) ? 'is-sel' : ''}>
                  <td className="w-check"><input type="checkbox" checked={selected.has(Number(v.id))} onChange={() => toggle(Number(v.id))} /></td>
                  <td><span className="u-call mono">{v.nom}</span></td>
                  <td className="muted">{v.type}</td>
                  <td>
                    <span className="st" style={{ background: st.bg, color: st.fg }}>
                      {materiel ? <span className="st__num">1M</span> : null}
                      <span className="st__lab">{st.label}</span>
                    </span>
                  </td>
                  <td>{inv ? <span className="mono" style={{ color: '#fca5a5' }}>{inv.numero_intervention || inv.id}</span> : <span className="muted">—</span>}</td>
                  <td className="mono">{crewCell(v)}</td>
                  <td className="muted">{buildingName(v.batiment_id)}</td>
                  <td className="mono muted">—</td>
                  <td className="ta-r mono">{eta != null && eta > 0 ? `${Math.floor(eta / 60)}:${String(eta % 60).padStart(2, '0')}` : '—'}</td>
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr><td colSpan={9}><div className="empty" style={{ margin: 8 }}>Aucun engin ne correspond au filtre.</div></td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {groups.length > 0 ? (
        <div className="grpbar">
          <span className="grpbar__t">Groupes</span>
          {groups.map((g) => (
            <button
              key={g.id}
              className="grp"
              style={{ ['--c' as string]: g.couleur } as React.CSSProperties}
              onClick={() => applyGroup(g.compo)}
              type="button"
            >
              {g.nom}
            </button>
          ))}
        </div>
      ) : null}

      <div className="units-actions">
        <span className="units-actions__count">{selected.size} engin{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}</span>
        <button className="btn btn--ghost" type="button">Départ type proposé</button>
        <button className="btn btn--ghost" type="button">Désengager</button>
        <button className="btn btn--primary" type="button" disabled={selected.size === 0 || !targetIntervention}>
          {targetIntervention ? `Engager sur ${targetIntervention.numero_intervention || targetIntervention.id}` : 'Engager'}
        </button>
      </div>
    </div>
  );
};

export default UnitsTable;
